/**
 * Unified catalog for Vybe Studio indicator browser (projects + marketplace templates).
 */

import { byteSize } from "./limits";
import { buildOwnershipTags, normalizeProject } from "./project-ownership";
import {
  INDICATOR_TEMPLATES,
  type IndicatorTemplate,
} from "./templates";
import type {
  CustomIndicatorBuild,
  CustomIndicatorProject,
  CustomIndicatorProjectKind,
} from "./types";

export type StudioCatalogFilter = "installed" | "my" | "marketplace";

export type StudioCatalogItemKind = "project" | "template";

export interface StudioCatalogItem {
  /** Stable list key: project id or `template:${templateId}` */
  id: string;
  kind: StudioCatalogItemKind;
  name: string;
  description: string;
  version: string;
  overlay: boolean;
  tags: string[];
  sizeBytes: number;
  createdAt: number;
  updatedAt: number;
  iconDataUrl?: string;
  projectKind: CustomIndicatorProjectKind;
  /** Present when kind === "project" */
  project?: CustomIndicatorProject;
  build?: CustomIndicatorBuild;
  /** Present when kind === "template" */
  template?: IndicatorTemplate;
  templateId?: string;
  entryContent?: string;
}

function templateTags(template: IndicatorTemplate): string[] {
  const tags = new Set<string>(["marketplace", "starter", "template"]);
  if (template.overlay) tags.add("overlay");
  else tags.add("pane");
  const words = template.name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2);
  for (const w of words) tags.add(w);
  if (template.id.includes("macd")) tags.add("macd");
  if (template.id.includes("rsi")) tags.add("rsi");
  if (template.id.includes("boll")) tags.add("bollinger");
  return [...tags];
}

export function buildTemplateReadme(template: IndicatorTemplate): string {
  return `# ${template.name}

${template.description}

## Starter template

Create a new project from this template in Vybe Studio, then customize the C# indicator and compile it in the browser.

| | |
|---|---|
| **Type** | ${template.overlay ? "Overlay on price chart" : "Separate indicator pane"} |
| **Version** | 1.0 |
| **Source** | Marketplace |
`;
}

function projectToCatalogItem(
  project: CustomIndicatorProject,
  build?: CustomIndicatorBuild
): StudioCatalogItem {
  const normalized = normalizeProject(project);
  const ownershipTags = buildOwnershipTags(normalized);
  const baseTags =
    normalized.tags.length > 0 ? normalized.tags : ["custom", "csharp"];
  return {
    id: normalized.id,
    kind: "project",
    name: normalized.name,
    description: normalized.description,
    version: normalized.version,
    overlay: normalized.overlay,
    tags: [...new Set([...baseTags, ...ownershipTags])],
    sizeBytes: normalized.sizeBytes,
    createdAt: normalized.createdAt,
    updatedAt: normalized.updatedAt,
    iconDataUrl: normalized.iconDataUrl,
    projectKind: normalized.kind,
    project: normalized,
    build,
    entryContent: normalized.entryContent,
  };
}

function templateToCatalogItem(template: IndicatorTemplate): StudioCatalogItem {
  const now = Date.now();
  return {
    id: `template:${template.id}`,
    kind: "template",
    name: template.name,
    description: template.description,
    version: "1.0",
    overlay: template.overlay,
    tags: templateTags(template),
    sizeBytes: byteSize(template.code),
    createdAt: now,
    updatedAt: now,
    projectKind: "indicator",
    template,
    templateId: template.id,
    entryContent: template.code,
  };
}

export function buildStudioCatalog(
  projects: CustomIndicatorProject[],
  buildsByProject: Record<string, CustomIndicatorBuild>
): StudioCatalogItem[] {
  const projectItems = projects.map((p) =>
    projectToCatalogItem(p, buildsByProject[p.id])
  );
  const templateItems = INDICATOR_TEMPLATES.map(templateToCatalogItem);
  return [...projectItems, ...templateItems];
}

export function filterCatalogBySection(
  items: StudioCatalogItem[],
  filter: StudioCatalogFilter
): StudioCatalogItem[] {
  switch (filter) {
    case "installed":
      return items.filter((i) => i.kind === "project" && i.build != null);
    case "my":
      return items.filter(
        (i) => i.kind === "project" && i.project?.owner === "user"
      );
    case "marketplace":
      return items.filter((i) => i.kind === "template");
    default:
      return items;
  }
}

export function searchCatalogItems(
  items: StudioCatalogItem[],
  query: string
): StudioCatalogItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => {
    const haystack = [
      item.name,
      item.description,
      item.version,
      item.projectKind,
      item.overlay ? "overlay" : "pane",
      item.kind === "template" ? "marketplace" : "my",
      item.build ? "installed" : "",
      ...item.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function defaultCatalogSelection(
  items: StudioCatalogItem[],
  filter: StudioCatalogFilter
): string | null {
  const section = filterCatalogBySection(items, filter);
  if (section.length > 0) return section[0]!.id;
  const installed = filterCatalogBySection(items, "installed");
  if (installed.length > 0) return installed[0]!.id;
  const my = filterCatalogBySection(items, "my");
  if (my.length > 0) return my[0]!.id;
  const marketplace = filterCatalogBySection(items, "marketplace");
  if (marketplace.length > 0) return marketplace[0]!.id;
  return null;
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatCatalogDate(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatBuildProvenance(build?: CustomIndicatorBuild): string {
  if (!build) return "Not built on this device";
  const when = new Date(build.builtAt).toLocaleString();
  const device = build.device?.label || build.device?.platform || "Unknown";
  return `Built ${when} on ${device}`;
}

export function sectionCount(
  items: StudioCatalogItem[],
  filter: StudioCatalogFilter
): number {
  return filterCatalogBySection(items, filter).length;
}
